import { Module } from "@/types/models/module"
import { Scene } from "@/types/models/scene"
import { Asset, Design, DesignGuidelines, ShowroomDesign } from "@/types/models/design"
import { Campaign, ShowroomCampaign } from "@/types/models/campaign"
import { AdTag } from "@/types/models/adtag"
import { AuthUser, CommonUser, EditedUser } from "@/types/models/user"
import { Group } from "@/types/models/group"
import { Comment } from "@/types/models/comment"
import { Event } from "@/types/models/event"
import { Notification } from "@/types/models/notification"
import { Template } from "@/types/models/template"
import { Modal } from "./types/modal"
import { DataBinding } from "./types/data-binding"
import { CampaignAnalytics, CurrentAnalytics } from "./types/analytics"
import { ShowroomPermissions, ShowroomDisplay } from "./types/showroom"
import { BuildNodeStatus, DraftEvent } from "./types/designer"

export interface State {
  // USER & AUTHENTICATION
  currentUser: AuthUser | null;
  editedUser: EditedUser | null;
  availableRoles: Array<{
    name: string;
    value: string;
  }>;
  availableCountries: Array<{
    title: string;
    value: string;
  }>;
  availableLanguages: Array<{
    title: string;
    value: string;
  }>;
  domainLimitedAccess: boolean;
  activeDomain: string | null;
  skipNavigationGuard: boolean;

  // DESIGNER
  activeDesign: Design | null;
  activeScene: string;
  activeTimelineTab: string | null;
  activeTimelineBlock: string | null;
  activeTimelineBlocks: string[];
  timelineFocused: boolean;
  timelinePlaying: boolean;
  currentTime: number;
  recordingTime: number;
  recordingModule: {
    moduleUuid: string;
    initData: {
      borderRadius: number;
      flipX: boolean;
      flipY: boolean;
      height: number;
      opacity: number;
      rotation: number;
      width: number;
      x: number;
      y: number;
    };
  } | null;
  editingCustomAnimation: {
    endProperties: boolean;
    property: {
      createdAt: string;
      duration: number;
      from: {
        borderRadius: number,
        height: number,
        opacity: number,
        rotation: number,
        width: number,
        x: number,
        y: number,
      },
      loopDelay: number,
      start: number,
      to: {
        borderRadius: number,
        height: number,
        opacity: number,
        rotation: number,
        width: number,
        x: number,
        y: number,
      },
      type: "mixed",
      uuid: string,
      updatedAt: string,
    };
  } | null;
  guidelines: DesignGuidelines;
  designerZoom: number;
  timelineScaleY: number;
  timelineScaleX: number;
  timelineOffset: number;
  collapsedTimelineLayers: string[];
  collapsedLayers: string[];
  timelineSettingsCollapsed: boolean;
  layerFolderIndex: number;
  layerInputCalls: number;
  renamingInputActive: boolean;
  draggedLayer: boolean;
  draggedModule: [string, string] | null;
  draggedAsset: Asset | null;
  selectedModule: string | null;
  currentScope: string | null;
  shiftPressed: boolean;
  ctrlPressed: boolean;
  firstStateSaved: boolean;
  lastSavedStateHash: string;
  stateId: string | null;
  locked: boolean;
  lockedBy: CommonUser | null,
  blockScreenshot: boolean;
  scenes: Scene[];
  modules: Module[];
  events: Event[];
  draftEvent: DraftEvent | null;

  // ASSETS
  assets: Asset[];
  deletedAssetsId: number[];
  replaceAssetUuid: string | null;
  assetUploadCallback: Function | null;
  currentDirectoryLevel: string;
  currentAssetsTree: Record<string, any>;
  loadingStatus: boolean;
  cropImgData: {
    designUuid?: string;
    id?: number;
    src?: string;
  };

  // SHOWROOM
  showroomActiveDesign: ShowroomDesign | null;
  showroomActiveCampaign: ShowroomCampaign | null;
  showroomActiveCampaignId: number | null;
  showroomPermissions: ShowroomPermissions;
  activeShowroomTheme: string;
  activeShowroomDisplay: ShowroomDisplay;
  hideEditor: boolean;
  selectedShowroomSettingsEditorMode: string;

  // ANALYTICS
  analytics: {
    startDate: string | null;
    endDate: string | null;
    campaignAnalytics: CampaignAnalytics;
    currentAnalytics: CurrentAnalytics;
    analyticsFetching: boolean;
  };

  // DATA BINDING
  dataBinding: DataBinding[];
  previewDataBinding: 0 | 1;

  // COLORS
  savedColors: string[];
  defaultPalette: string[];

  // FEATURES
  availableFeatures: string[];
  allFeatures: string[];

  // MODAL
  modal: Modal;

  // COMMENTS
  showResolvedComments: boolean;
  currentlyEditedCommentUuid: string | null;

  // FILTERS & SEARCH
  selectedFilters: {
    role: { name: string; value: string; }[];
    campaign: { name: string; value: string; }[];
    tag: { name: string; value: string; }[];
    status: { name: string; value: string; }[];
    brand: { name: string; value: string; }[];
    user: { name: string; value: string; }[];
    group: { name: string; value: string; }[];
  };
  searchResult: Record<string, any>;

  // SETTINGS
  settingsContext: string;
  settingsObject: Record<string, any>;

  // DASHBOARD
  selectedDashboard: string;

  // BUILD NODE
  buildNodeStatus: BuildNodeStatus;

  // COLLECTIONS
  groups: Group[];
  users: any[];
  reports: any[];
  templates: Template[];
  designs: Design[];
  adtags: AdTag[];
  hostedDesigns: any[];
  invoices: any[];
  campaigns: any[];
  billables: any[];
  liveCampaigns: any[];
  hostedCampaigns: any[];
  companies: [];
  comments: Comment[];
  notifications: Notification[];
  availableBrands: any[]

  // ACTIVE ENTITIES
  // @TODO: invoices are not even used in the app
  activeInvoice: any | null;
  activeCampaign: Campaign | null;
  activeCampaignId: number | null;
  activeTemplateId: number | null;
  selectedTag: AdTag | null;
  companyTags: string[];

  // MISC
  name: string;
  packageId: string | number;
  loading: boolean;
}

const state: State = {
  // USER & AUTHENTICATION
  currentUser: null,
  editedUser: null,
  availableRoles: [
    {
      name: "Super Admin",
      value: "super_admin"
    },
    {
      name: "Admin",
      value: "admin"
    },
    {
      name: "Manager",
      value: "manager"
    },
    {
      name: "Designer",
      value: "user"
    },
    {
      name: "Publisher",
      value: "publisher"
    }
  ],
  availableCountries: [{
    title: "Germany",
    value: "DE"
  }],
  availableLanguages: [{
    title: "English",
    value: "en"
  }],
  domainLimitedAccess: false,
  activeDomain: null,
  skipNavigationGuard: false,

  // DESIGNER
  activeDesign: null,
  activeScene: "", // Active scene uuid
  activeTimelineTab: null,
  activeTimelineBlock: null,
  activeTimelineBlocks: [],
  timelineFocused: false,
  timelinePlaying: false,
  currentTime: 0,
  recordingTime: -1,
  recordingModule: null,
  editingCustomAnimation: null,
  guidelines: {
    horizontal: [],
    vertical: [],
    horizontalLocked: [],
    verticalLocked: []
  },
  designerZoom: 1,
  timelineScaleY: 1.0,
  timelineScaleX: 1.0,
  timelineOffset: 0,
  collapsedTimelineLayers: [],
  collapsedLayers: [],
  timelineSettingsCollapsed: true,
  layerFolderIndex: 0,
  layerInputCalls: 0,
  renamingInputActive: false,
  draggedLayer: false,
  draggedModule: null,
  draggedAsset: null,
  selectedModule: null, // used to determine last actively selected module
  currentScope: null, // used in SwiperGroupModule child element add,
  shiftPressed: false,
  ctrlPressed: false,
  firstStateSaved: false,
  lastSavedStateHash: "",
  stateId: null,
  locked: false,
  lockedBy: null,
  blockScreenshot: false,
  scenes: [],
  modules: [],
  events: [],

  // ASSETS
  assets: [],
  deletedAssetsId: [],
  replaceAssetUuid: null,
  assetUploadCallback: null,
  currentDirectoryLevel: "/",
  currentAssetsTree: {},
  loadingStatus: false, // Loading status of uploaded assets
  cropImgData: {},

  // SHOWROOM
  showroomActiveDesign: null,
  showroomActiveCampaign: null,
  showroomActiveCampaignId: null,
  showroomPermissions: {
    create: false,
    view: false,
    edit: false,
    publish: false,
    delete: false
  },
  activeShowroomTheme: "dark",
  activeShowroomDisplay: {
    displayType: "phone",
    variantType: {
      type: "s",
      name: "Small",
      resolution: [320, 480]
    }
  },
  hideEditor: false,
  selectedShowroomSettingsEditorMode: "basic",

  // ANALYTICS
  analytics: {
    startDate: null,
    endDate: null,
    campaignAnalytics: {
      displays: 0,
      clicks: 0,
      clickrate: 0,
      users: 0
    },
    currentAnalytics: {
      displays: 0,
      clicks: 0,
      clickrate: 0,
      users: 0
    },
    analyticsFetching: false
  },

  // DATA BINDING
  dataBinding: [], // Contains CURRENT user-defined variables (available in current scope),
  previewDataBinding: 0,
  draftEvent: {
    action: {},
    actionTarget: null,
    conditions: [],
    delay: null,
    target: "",
    trigger: null,
    uuid: null
  },

  // COLORS
  savedColors: [],
  defaultPalette: [
    "#d4ff00",
    "#D0021B",
    "#F5A623",
    "#F8E71C",
    "#8B572A",
    "#7ED321",
    "#417505",
    "#BD10E0",
    "#9013FE",
    "#4A90E2",
    "#50E3C2",
    "#B8E986",
    "#000000",
    "#4A4A4A",
    "#9B9B9B",
    "#FFFFFF",
    "#00000000"
  ],

  // FEATURES
  availableFeatures: [],
  allFeatures: [],

  // MODAL
  modal: {
    isOpen: false,
    view: {},
    options: {},
    actions: []
  },

  // COMMENTS
  showResolvedComments: false,
  currentlyEditedCommentUuid: null,

  // FILTERS & SEARCH
  selectedFilters: {
    role: [] as { name: string; value: string; }[],
    campaign: [] as { name: string; value: string; }[],
    tag: [] as { name: string; value: string; }[],
    status: [] as { name: string; value: string; }[],
    brand: [] as { name: string; value: string; }[],
    user: [] as { name: string; value: string; }[],
    group: [] as { name: string; value: string; }[]
  },
  searchResult: {},

  // SETTINGS
  settingsContext: "", // manipulate for changing context of edited settings
  settingsObject: {},

  // DASHBOARD
  selectedDashboard: "designer",

  // BUILD NODE
  buildNodeStatus: {
    connected: false,
    count: 0
  },

  // COLLECTIONS
  groups: [],
  users: [],
  reports: [],
  templates: [],
  designs: [],
  adtags: [],
  hostedDesigns: [],
  invoices: [],
  campaigns: [],
  billables: [],
  liveCampaigns: [],
  hostedCampaigns: [],
  companies: [],
  comments: [],
  notifications: [],
  availableBrands: [],

  // ACTIVE ENTITIES
  activeInvoice: null,
  activeCampaign: null,
  activeCampaignId: null,
  activeTemplateId: null,
  selectedTag: null,
  companyTags: [],

  // MISC
  name: "",
  packageId: "",
  loading: true
}

export default state
